import React from 'react'
import PropTypes from 'prop-types'
import { useQuery } from '@apollo/client'
import { Bar } from '@ant-design/charts'
import { Card, Col, Collapse, Row, Spin, Typography } from 'antd'
import {
  DISH_LIST,
  MENU,
  MENU_LIST,
  SCRAPING_TAG_TYPES,
  SECTION_LIST,
} from '../../../constants'
import { SCRAPING_SESSION_TAG_SCORES } from '../../../graphql/queries'
import { formatMetricPercent } from '../../../utils'

const tagScoreGroups = [
  {
    title: 'General tags (menu type, structure, flags)',
    includes: type =>
      ![MENU, MENU_LIST, SECTION_LIST, DISH_LIST].includes(type),
  },
  {
    title: 'Menu, menu list and section list',
    includes: type => [MENU, MENU_LIST, SECTION_LIST].includes(type),
  },
  {
    title: 'Dish list',
    includes: type => type === DISH_LIST,
  },
]

const ScrapingSessionTagScores = ({ sessionId, isValidated }) => {
  const {
    data: { scrapingSessionTagScores: scores = [] } = {},
    loading,
    error,
  } = useQuery(SCRAPING_SESSION_TAG_SCORES, {
    variables: { sessionId },
    fetchPolicy: 'no-cache',
    skip: !isValidated,
  })

  const data = scores.map(
    ({ type, name, count, averageValidationScore, averageSimilarity }) => ({
      type,
      tag: `${SCRAPING_TAG_TYPES[type]?.name || type} ${name}`,
      score: averageValidationScore * 100,
      count,
      averageSimilarity,
    }),
  )

  const renderContent = () => {
    if (!isValidated) {
      return (
        <Typography.Text type="secondary">
          Validation scores are not available yet.
        </Typography.Text>
      )
    }

    if (loading) {
      return (
        <Row justify="center">
          <Spin tip="Loading..." />
        </Row>
      )
    }

    if (error) {
      return (
        <Typography.Text type="danger">
          Failed to load tag scores.
        </Typography.Text>
      )
    }

    if (!data.length) {
      return (
        <Typography.Text type="secondary">
          No scored tags are available yet.
        </Typography.Text>
      )
    }

    return (
      <Row gutter={[16, 16]}>
        {tagScoreGroups.map(({ title, includes }) => {
          const groupData = data.filter(({ type }) => includes(type))

          return (
            <Col xs={24} md={12} xl={8} key={title}>
              <Card size="small" title={title}>
                {groupData.length ? (
                  <div style={{ maxHeight: 400, overflowY: 'auto' }}>
                    <Bar
                      data={groupData}
                      height={Math.max(300, groupData.length * 36)}
                      xField="score"
                      yField="tag"
                      yAxis={{
                        verticalLimitLength: 0.3,
                        label: { autoEllipsis: true, autoHide: false },
                      }}
                      legend={false}
                      tooltip={{
                        customItems: items => {
                          const [scoreItem] = items
                          if (!scoreItem) return items

                          return [
                            {
                              ...scoreItem,
                              name: 'Count',
                              value: scoreItem.data.count,
                            },
                            scoreItem,
                            {
                              ...scoreItem,
                              name: 'Average similarity',
                              value: formatMetricPercent(
                                scoreItem.data.averageSimilarity,
                              ),
                            },
                          ]
                        },
                      }}
                      meta={{
                        score: {
                          alias: 'Average validation score',
                          min: 0,
                          max: 100,
                          formatter: value => `${Number(value).toFixed(1)}%`,
                        },
                      }}
                    />
                  </div>
                ) : (
                  <Typography.Text type="secondary">
                    No scored tags in this group.
                  </Typography.Text>
                )}
              </Card>
            </Col>
          )
        })}
      </Row>
    )
  }

  return (
    <Collapse style={{ marginTop: 16 }}>
      <Collapse.Panel header="Average validation score by tag" key="tag-scores">
        {renderContent()}
      </Collapse.Panel>
    </Collapse>
  )
}

ScrapingSessionTagScores.propTypes = {
  sessionId: PropTypes.string.isRequired,
  isValidated: PropTypes.bool.isRequired,
}

export default React.memo(ScrapingSessionTagScores)
